import * as actions_timeclock from 'actions/timeclock-actions';
import * as toolbox from 'components/common/toolbox';
import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Accordion } from 'react-bootstrap';
import { CloseX, Input, Spinner } from 'enspire-manager-framework';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

const advancedFormat = require("dayjs/plugin/advancedFormat");
const duration = require("dayjs/plugin/duration");
const utc = require("dayjs/plugin/utc");
dayjs.extend(advancedFormat);
dayjs.extend(duration);
dayjs.extend(utc);

/**
 * TimeCardDetail Component
 * 
 * When displayed from the Timeclock, Timesheet data is loaded for the current week and provides functionality to navigate through 
 * different time periods, view and edit time entries, and print timesheets or timecards. 
 * 
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.source - Specifies the source of the data ('timeclock' or 'timecards').
 * @param {string} [props.user_id] - The ID of the user for whom the timecards are being displayed (optional).
 * 
 * @returns {JSX.Element} The rendered TimeCardDetail component.
 * 
 * @example
 * <TimeCardDetail source="timeclock" />
 * 
 */
export default function TimeCardDetail(props) {
	
	const seg = 4;
	const params = useParams();
	const history = useHistory();
	const dispatch = useDispatch();
	const routeLocation = useLocation();

    const employees = useSelector((store) => store.employees.employees);
    const users = useSelector(store => store.users);
    const user_permissions = useSelector(store => store.users?.user_permissions);
	const timeClockSettings = useSelector(store => store.settings.settings.timeclock)
	const timeSheet = useSelector(store => store.users?.time_sheet?.[0]) ?? {};
    const checkedIn = useSelector(store => store.users?.time_sheet?.[1]) ?? {};

	const start_work_week = (timeClockSettings?.start_work_week) ? parseInt(timeClockSettings?.start_work_week) : 1;

    const [open, setOpen] = useState('');
	const [startDate, setStartDate] = useState(params.tab_id || dayjs().day(start_work_week).startOf('day').format('YYYY-MM-DD'));

	const fromTimeClock = (props.source == 'timeclock'); // current user
	const singleEmployee = (props.source == 'timecards' || fromTimeClock);
	const user_id = (fromTimeClock) ? users.user.id : props.user_id;
	const isCurrent = (startDate == dayjs().day(start_work_week).format('YYYY-MM-DD'));

	/* Effects -----------------------------------------------------------------------------------------------------------------*/
	
	// Get rendered Timesheets & Timecards
	useEffect(() => {
		if (props.source == 'timecards') dispatch(actions_timeclock.subTimeCards(params.handle, user_id));
		return () => {
			var unsubscribe = users.time_cards_unsubscribe;
			if (typeof unsubscribe === 'function') unsubscribe();
		};
	}, [params.appId, params.handle, props.user_id]);

	useEffect(() => {
		if (props.source == 'timesheets') dispatch(actions_timeclock.subTimeSheets(params.handle));
		return () => {
			var unsubscribe = users.time_sheets_unsubscribe;
			if (typeof unsubscribe === 'function') unsubscribe();
		};
	}, [params.appId, params.handle]);
	
	useEffect(() => {
		setStartDate((params.tab_id) ? params.tab_id : dayjs().day(start_work_week).startOf('day').format('YYYY-MM-DD'));
	}, [params.tab_id]);

	// Get selected week timesheet data
	useEffect(() => {
		if (startDate) dispatch(actions_timeclock.getTimeSheetData(params.handle, [dayjs(startDate).toDate(), dayjs(startDate).add(6, 'day').endOf('day').toDate()]));
	}, [startDate]);

	/* Handlers --------------------------*/

	const handleInc = () => {
		let date = dayjs(startDate).add(1, 'week').format('YYYY-MM-DD');
		setStartDate(date);
		let location = toolbox.modifyPath(routeLocation.pathname, seg+3, date); 
		if (params.form != 'overview')  history.replace({ pathname: location });
	}
	const handleDec = () => {
		let date = dayjs(startDate).subtract(1, 'week').format('YYYY-MM-DD');
		setStartDate(date);
		let location = toolbox.modifyPath(routeLocation.pathname, seg+3, date);
		if (params.form != 'overview')  history.replace({ pathname: location });
	}
	const handleDate = (date) => {
		setStartDate(date);
		let location = toolbox.modifyPath(routeLocation.pathname, seg+3, date); 
		if (params.form != 'overview')  history.replace({ pathname: location });
	}
	const handleOpenAccordian = (tab) => {
        if (open == tab) setOpen("");
        else setOpen(tab);
	}
	const openEditTimeClock = (id) => {
		let location = toolbox.modifyPath(routeLocation.pathname, seg, id + '/time-clock/' + dayjs(startDate).format('X') + '/' + dayjs(startDate).add(6, 'day').format('X')); 
		history.push({ pathname: location });
	}

	/* Generate Timecard Components -----------------------------------------------------------------------------------------------------------------*/
	
	const [timecards_array, totalTime] = actions_timeclock.returnTimecardComponents({
		checkedIn,
		employees,
		handleOpenAccordian,
		isCurrent,
		open,
		openEditTimeClock, 
		timeSheet,
		user_id: user_id,
		user_permissions, 
	});
	
	/* Constants --------------------------*/

	const timesheet = _.find(users.time_sheets, { id: startDate });
	const timecard = _.find(users.time_cards, (o) => o.id.startsWith(startDate));

	return (

		<div className={ 'col-lg-12'}>

			{/* Header -------------------------------------------------------------------------------- */}

			<div className="d-flex justify-content-between flex-wrap mt-3">

				<h2 className="mb-0 mt-1 mb-2 ml-2 mr-2">
					{(isCurrent) ? 'Current Time Period:' : 'Starting Time Period:' } 
				</h2>
				<Input
					className="ml-2 mr-auto"
					name={'renderDate'}
					noLabel={true}
					defaultValue={null}
					filterDate={(moment) => { if (dayjs(moment).day() != start_work_week) return true }}
					onChange={(field, date) => handleDate(dayjs(date).format('YYYY-MM-DD'))}
					type="date"
					value={startDate}
				/>
				
				{ timesheet && !singleEmployee &&
					<a className="btn btn-secondary mr-4 ml-auto" href={ timesheet?.downloadUrl } target="_blank">
						<i className="fas fa-print mr-1"></i> Timesheet
					</a>
				}
				{ timecard && singleEmployee &&
					<a className="btn btn-secondary mr-4 ml-auto" href={ timecard.downloadUrl } target="_blank">
						<i className="fas fa-print mr-1"></i> Timecard
					</a>
				}
				<div>
					<i className="fa-solid fa-chevron-left text-muted fa-2x mt-1 px-1" role="button" onClick={handleDec}></i>
					{ startDate < dayjs().day(start_work_week).format('YYYY-MM-DD')
						? <i className="fa-solid fa-chevron-right text-muted fa-2x mt-1 px-1 mr-2" role="button" onClick={handleInc}></i>
						: <i className="fa-solid fa-chevron-right fa-2x mt-1 px-1 mr-2" style={{ color: '#dddddd' }}></i>
					}
				</div>
			</div>
			<div style={{ clear: 'both' }}></div>

			<ul className="sortable-list agile-list mt-3" id="todo">

				{/* TimeCard(s) ------------------------------------------------------------------------- */}
				
				{ users.time_sheet_pending
					? 	<Spinner />
					: 	<Accordion  defaultActiveKey={ (singleEmployee) ? '0': '' }>
							{timecards_array}
						</Accordion>
				}
				{ !timecards_array.length &&
					<p className="ml-3 mb-4">-- No Completed Time Entries --</p>
				}

				{/* Total ------------------------------------------------------------------------------- */}

				<li className="warning-element ui-sortable-handle mt-3 animated fadeIn animation-delay-4">
					<div className="agile-detail">
						<span className="float-right" style={{ fontSize: '18px' }}>
							{ toolbox.formatHoursMinutes(totalTime) } <small>hrs</small>
						</span>
						<h2 className="m-0">Total</h2>
					</div>
				</li>
			</ul>
		</div>
	);
};

